import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  SortingState,
} from '@tanstack/react-table';
import { GET_LINK_BY_AGENT_ID, CANCEL_SCHEDULE } from '~/services/schedule';
import { publicClient } from '~/services';
import CancelAppointmentDialog from '../../../shared/CancelAppointmentDialog';

interface ScheduleLink {
  id: string;
  name: string;
  phone: string;
  createdAt: Date;
  status: 'pending' | 'scheduled' | 'cancelled' | 'completed';
  scheduledDate?: Date;
  scheduleName?: string; // Si no tiene, es "General"
  link: string;
  calendar_rulers_id?: string;
  agent_id: string;
  cancel_reason?: string; // Razón de cancelación
  cancel_at?: Date; // Fecha de cancelación
  cancel_by?: string; // Quién canceló
}

const ManageSchedules: React.FC = () => {
  const [t] = useTranslation('schedule');
  const { agentId } = useParams();
  const [scheduleLinks, setScheduleLinks] = useState<ScheduleLink[]>([]);
  const [filter, setFilter] = useState<'all' | 'pending' | 'scheduled' | 'cancelled' | 'completed'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [sorting, setSorting] = useState<SortingState>([{ id: 'createdAt', desc: true }]); // Ordenamiento inicial
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<ScheduleLink | null>(null);
  const [isInfoDialogOpen, setIsInfoDialogOpen] = useState(false);
  const [selectedCancelledAppointment, setSelectedCancelledAppointment] = useState<ScheduleLink | null>(null);

  const { data, loading: fetchLoading, refetch } = useQuery(GET_LINK_BY_AGENT_ID, {
    variables: {
      data: {
        agent_id: agentId,
      },
    },
    fetchPolicy: 'network-only',
    skip: !agentId,
  });

  const [cancelScheduleMutation, { loading: cancelLoading }] = useMutation(CANCEL_SCHEDULE, {
    client: publicClient,
  });

  // Process the data when it arrives
  useEffect(() => {
    if (data?.get_link_by_agent_id?.data && !data.get_link_by_agent_id.hasError) {
      const links = data.get_link_by_agent_id.data;
      
      const formattedLinks: ScheduleLink[] = links.map((link: any) => {
        // Calculate scheduled date
        let scheduledDate: Date | undefined = undefined;
        if ((link.status === 'ACCEPTED' || link.status === 'COMPLETED') && link.start_date?.date) {
          scheduledDate = new Date(link.start_date.date);
        }

        return {
          id: link._id || link.id,
          name: link.name || '',
          phone: link.people_channel_id?.channel_id || '',
          createdAt: link.created_at ? new Date(link.created_at) : new Date(),
          status: link.status === 'PENDING' ? 'pending' : 
                  link.status === 'ACCEPTED' ? 'scheduled' : 
                  link.status === 'COMPLETED' ? 'completed' :
                  link.status === 'REJECTED' ? 'cancelled' : 'pending',
          scheduledDate,
          scheduleName: link.calendar_rulers_id?.title || undefined,
          link: `${window.location.origin}/calendar/${link._id}` || '',
          calendar_rulers_id: link.calendar_rulers_id?._id || undefined,
          agent_id: link.agent_id || agentId,
          cancel_reason: link.cancel_reason || undefined,
          cancel_at: link.cancel_at ? new Date(link.cancel_at) : undefined,
          cancel_by: link.cancel_by || undefined,
        };
      });
      // No aplicar ordenamiento manual aquí, dejar que TanStack Table lo maneje
      setScheduleLinks(formattedLinks);
    } else if (data && data.get_link_by_agent_id?.hasError) {
      console.error('Error fetching schedule links:', data?.get_link_by_agent_id?.message);
      setScheduleLinks([]);
    }
  }, [data, agentId]);

  const filteredLinks = useMemo(() => {
    return scheduleLinks.filter(link => {
      const matchesFilter = filter === 'all' || link.status === filter;
      const matchesSearch = searchTerm === '' || 
        link.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        link.phone.includes(searchTerm) ||
        (link.scheduleName && link.scheduleName.toLowerCase().includes(searchTerm.toLowerCase()));
      
      return matchesFilter && matchesSearch;
    });
  }, [scheduleLinks, filter, searchTerm]);

  const getStatusText = useCallback((status: string) => {
    const statusClasses = {
      pending: 'text-warning',
      scheduled: 'text-info',
      cancelled: 'text-error',
      completed: 'text-success',
    };
    const statusLabels = {
      pending: t('pending') || 'Pendiente',
      scheduled: t('scheduled') || 'Agendado',
      cancelled: t('cancelled') || 'Cancelada',
      completed: t('completed') || 'Completada',
    };
    const normalizedStatus = status.toLowerCase();
    return (
      <span className={`font-bold ${statusClasses[normalizedStatus as keyof typeof statusClasses] || 'text-warning'}`}>
        {statusLabels[normalizedStatus as keyof typeof statusLabels] || status}
      </span>
    );
  }, [t]);

  const formatDate = useCallback((date: Date) => {
    return new Intl.DateTimeFormat('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true // Use 12-hour format with AM/PM
    }).format(date);
  }, []);

  const copyToClipboard = useCallback(async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      setSuccessMessage(t('copy_schedule_link') + ' ' + (t('link_generated') || 'exitoso'));
      
      // Auto-hide success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      console.error('Failed to copy link:', err);
      // Could add error toast here
    }
  }, [t]);

  const handleCancelAppointment = useCallback((link: ScheduleLink) => {
    if (link.status === 'scheduled') {
      setSelectedAppointment(link);
      setIsCancelDialogOpen(true);
    }
  }, []);

  const handleConfirmCancellation = useCallback(async (reason: string) => {
    if (!selectedAppointment) return;

    try {
      console.log('Canceling appointment:', {
        appointmentId: selectedAppointment.id,
        reason,
        appointmentDetails: {
          name: selectedAppointment.name,
          phone: selectedAppointment.phone,
          scheduledDate: selectedAppointment.scheduledDate,
        }
      });

      // Call the cancel schedule mutation
      const { data: cancelResult } = await cancelScheduleMutation({
        variables: {
          data: {
            schedule_id: selectedAppointment.id,
            cancel_reason: reason,
            cancel_by: 'HUMAN_AGENT', // Since this is being cancelled by the agent from the admin panel
          }
        }
      });

      if (cancelResult?.cancel_schedule?.hasError) {
        throw new Error(cancelResult.cancel_schedule.message || 'Error canceling appointment');
      }

      // Remove the canceled appointment from the list completely
      setScheduleLinks(prevLinks => 
        prevLinks.filter(link => link.id !== selectedAppointment.id)
      );

      setSuccessMessage(t('appointment_canceled_successfully') || 'Cita cancelada exitosamente');
      
      // Auto-hide success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);

      // Refetch data to ensure UI is in sync with backend
      if (refetch) {
        refetch();
      }

    } catch (error) {
      console.error('Error canceling appointment:', error);
      // Show error message to user
      setSuccessMessage(null);
      // TODO: You might want to add an error state and show error toast
      alert(`Error al cancelar la cita: ${error instanceof Error ? error.message : 'Error desconocido'}`);
    }
  }, [selectedAppointment, t, cancelScheduleMutation, refetch]);

  const handleCloseCancelDialog = useCallback(() => {
    setIsCancelDialogOpen(false);
    setSelectedAppointment(null);
  }, []);

  const handleShowCancelInfo = useCallback((link: ScheduleLink) => {
    setSelectedCancelledAppointment(link);
    setIsInfoDialogOpen(true);
  }, []);

  const handleCloseInfoDialog = useCallback(() => {
    setIsInfoDialogOpen(false);
    setSelectedCancelledAppointment(null);
  }, []);



  const columnHelper = createColumnHelper<ScheduleLink>();

  const columns = useMemo(() => [
    columnHelper.accessor('name', {
      header: () => t('name') || 'Nombre',
      cell: info => (
        <span className="font-medium text-brand-heading">{info.getValue()}</span>
      ),
      enableSorting: true,
    }),
    columnHelper.accessor('phone', {
      header: () => t('phone_number') || 'Teléfono',
      cell: info => (
        <a href={`tel:${info.getValue()}`} className="link link-primary">
          {info.getValue()}
        </a>
      ),
      enableSorting: true,
    }),
    columnHelper.accessor('createdAt', {
      header: () => t('created_date') || 'Fecha Creación',
      cell: info => (
        <span className="text-sm text-brand-caption">
          {formatDate(info.getValue())}
        </span>
      ),
      enableSorting: true,
      sortingFn: 'datetime',
    }),
    columnHelper.accessor('status', {
      header: () => t('status') || 'Estado',
      cell: info => getStatusText(info.getValue()),
      enableSorting: true,
    }),
    columnHelper.accessor('scheduleName', {
      header: () => t('schedule_name') || 'Agenda',
      cell: info => (
        <span className="text-brand-body">
          {info.getValue() || 'General'}
        </span>
      ),
      enableSorting: true,
    }),
    columnHelper.accessor('scheduledDate', {
      header: () => t('scheduled_date') || 'Fecha Agendada',
      cell: info => {
        const link = info.row.original;
        return (
          <span className="text-sm text-brand-caption">
            {(link.status === 'scheduled' || link.status === 'completed') && info.getValue()
              ? formatDate(info.getValue()!)
              : null}
          </span>
        );
      },
      enableSorting: true,
      sortingFn: 'datetime',
    }),
    columnHelper.display({
      id: 'actions',
      header: () => t('actions') || 'Acciones',
      cell: info => {
        const link = info.row.original;
        return (
          <div className="flex gap-1">
            <button
              onClick={() => copyToClipboard(link.link)}
              className="btn btn-ghost btn-xs"
              title={t('copy_schedule_link') || 'Copiar enlace'}
              disabled={link.status === 'cancelled' || link.status === 'completed'}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </button>
            <button
              onClick={() => window.open(link.link, '_blank')}
              className="btn btn-ghost btn-xs"
              title={t('open_link') || 'Abrir enlace'}
              disabled={link.status === 'cancelled' || link.status === 'completed'}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </button>
            {link.status === 'scheduled' && (
              <button
                onClick={() => handleCancelAppointment(link)}
                className="btn btn-ghost btn-xs"
                title={t('cancel_appointment') || 'Cancelar cita'}
                disabled={cancelLoading}
              >
                {cancelLoading ? (
                  <span className="loading loading-spinner loading-xs"></span>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                )}
              </button>
            )}
            {link.status === 'cancelled' && link.cancel_reason && (
              <button
                onClick={() => handleShowCancelInfo(link)}
                className="btn btn-ghost btn-xs"
                title={t('view_cancel_reason') || 'Ver razón de cancelación'}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-info" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
            )}
          </div>
        );
      },
      enableSorting: false,
    }),
  ], [t, getStatusText, formatDate, copyToClipboard]);

  const table = useReactTable({
    data: filteredLinks,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSortingRemoval: false,
  });

  const getStatusCounts = () => {
    return {
      all: scheduleLinks.length,
      pending: scheduleLinks.filter(l => l.status === 'pending').length,
      scheduled: scheduleLinks.filter(l => l.status === 'scheduled').length,
      cancelled: scheduleLinks.filter(l => l.status === 'cancelled').length,
      completed: scheduleLinks.filter(l => l.status === 'completed').length,
    };
  };

  const statusCounts = getStatusCounts();


    return (
      <div className="flex flex-col">
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold mb-3 text-brand-heading">{t('manage_links') || 'Gestionar Enlaces'}</h2>
            <p className="text-brand-body">
              {t('manage_links_description') || 'Administra todos los enlaces de agenda creados y su estado actual.'}
            </p>
          </div>
          <button 
            className="btn btn-sm btn-outline" 
            onClick={() => refetch()}
            disabled={fetchLoading}
          >
            {fetchLoading ? (
              <span className="loading loading-spinner loading-xs"></span>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            )}
            <span className="ml-1">{t('refresh') || 'Actualizar'}</span>
          </button>
        </div>
      </div>

      {/* Statistics summary */}
      {!fetchLoading && scheduleLinks.length > 0 && (
        <div className="mb-8 stats stats-vertical lg:stats-horizontal shadow bg-base-100">
          <div className="stat">
            <div className="stat-title text-brand-caption">{t('total_links') || 'Total Enlaces'}</div>
            <div className="stat-value text-brand-primary">{statusCounts.all}</div>
          </div>
          <div className="stat">
            <div className="stat-title text-brand-caption">{t('pending_links') || 'Pendientes'}</div>
            <div className="stat-value text-warning">{statusCounts.pending}</div>
          </div>
          <div className="stat">
            <div className="stat-title text-brand-caption">{t('scheduled_links') || 'Agendados'}</div>
            <div className="stat-value text-info">{statusCounts.scheduled}</div>
          </div>
          <div className="stat">
            <div className="stat-title text-brand-caption">{t('completed_links') || 'Completadas'}</div>
            <div className="stat-value text-success">{statusCounts.completed}</div>
          </div>
          <div className="stat">
            <div className="stat-title text-brand-caption">{t('cancelled_links') || 'Canceladas'}</div>
            <div className="stat-value text-error">{statusCounts.cancelled}</div>
          </div>
        </div>
      )}

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {(['all', 'pending', 'scheduled', 'completed', 'cancelled'] as const).map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`btn btn-sm ${filter === status ? 'btn-primary rounded-full' : 'btn btn-outline'}`}
          >
            {status === 'all' ? t('all') || 'Todos' : 
             status === 'pending' ? t('pending') || 'Pendientes' :
             status === 'scheduled' ? t('scheduled') || 'Agendados' :
             status === 'completed' ? t('completed') || 'Completadas' :
             status === 'cancelled' ? t('cancelled') || 'Canceladas' : status}
            <span className="ml-1 badge badge-neutral badge-sm">{statusCounts[status]}</span>
          </button>
        ))}
      </div>

      {/* Search bar */}
      <div className="mb-6">
        <div className="form-control w-full max-w-md">
          <input
            type="text"
            placeholder={t('search_by_name_phone_or_schedule') || 'Buscar por nombre, teléfono o agenda...'}
            className="input input-bordered border-brand-primary focus:border-brand-secondary focus:outline-none focus:ring-2 focus:ring-brand-secondary/20 w-full"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Links table */}
      {fetchLoading ? (
        <div className="overflow-x-auto">
          <table className="table w-full">
            <thead>
              <tr>
                <th>{t('name') || 'Nombre'}</th>
                <th>{t('phone_number') || 'Teléfono'}</th>
                <th>{t('created_date') || 'Fecha Creación'}</th>
                <th>{t('status') || 'Estado'}</th>
                <th>{t('schedule_name') || 'Agenda'}</th>
                <th>{t('scheduled_date') || 'Fecha Agendada'}</th>
                <th>{t('actions') || 'Acciones'}</th>
              </tr>
            </thead>
            <tbody>
              {[1, 2, 3].map((item) => (
                <tr key={item} className="animate-pulse">
                  <td><div className="h-4 bg-gray-200 rounded w-24"></div></td>
                  <td><div className="h-4 bg-gray-200 rounded w-32"></div></td>
                  <td><div className="h-4 bg-gray-200 rounded w-20"></div></td>
                  <td><div className="h-6 bg-gray-200 rounded w-16"></div></td>
                  <td><div className="h-4 bg-gray-200 rounded w-20"></div></td>
                  <td><div className="h-4 bg-gray-200 rounded w-24"></div></td>
                  <td><div className="h-8 bg-gray-200 rounded w-16"></div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : filteredLinks.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="table w-full border border-brand-primary/20 rounded-lg">
            <thead>
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id} className="bg-brand-primary/5">
                  {headerGroup.headers.map(header => (
                    <th 
                      key={header.id}
                      className={`text-brand-heading font-semibold border-b border-brand-primary/20 ${
                        header.column.getCanSort() ? 'cursor-pointer select-none hover:bg-brand-primary/10' : ''
                      }`}
                      onClick={header.column.getToggleSortingHandler()}
                      title={header.column.getCanSort() ? t('click_to_sort') || 'Haz clic para ordenar' : undefined}
                    >
                      <div className="flex items-center gap-2">
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                        {header.column.getCanSort() && (
                          <span className="text-brand-caption">
                            {{
                              asc: '↑',
                              desc: '↓',
                            }[header.column.getIsSorted() as string] ?? '↕'}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map(row => (
                <tr key={row.id} className="hover:bg-brand-primary/5 transition-colors">
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id} className="border-b border-brand-primary/10">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center p-10 bg-base-200 rounded-lg">
          <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-brand-accent mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
          <p className="text-lg text-brand-body mb-2">
            {searchTerm || filter !== 'all' 
              ? (t('no_links_found') || 'No se encontraron enlaces')
              : (t('no_links_created') || 'No hay enlaces creados')
            }
          </p>
          {searchTerm && (
            <button 
              onClick={() => setSearchTerm('')}
              className="btn btn-sm btn-ghost"
            >
              {t('clear_search') || 'Limpiar búsqueda'}
            </button>
          )}
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="toast toast-end toast-top z-50 mr-4 mt-4">
          <div className="alert alert-success text-success-content shadow-lg animate-fadeInRight">
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 stroke-current" fill="none" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-medium">{successMessage}</span>
              </div>
              <button 
                onClick={() => setSuccessMessage(null)} 
                className="ml-4 hover:opacity-75 transition-opacity"
                aria-label="Cerrar notificación"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 stroke-current" fill="none" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Appointment Dialog */}
      {selectedAppointment && (
        <CancelAppointmentDialog
          isOpen={isCancelDialogOpen}
          onClose={handleCloseCancelDialog}
          appointmentName={selectedAppointment.name}
          appointmentPhone={selectedAppointment.phone}
          scheduledDate={selectedAppointment.scheduledDate}
          onConfirmCancel={handleConfirmCancellation}
        />
      )}

      {/* Cancel Information Dialog */}
      {selectedCancelledAppointment && (
        <div className={`modal ${isInfoDialogOpen ? 'modal-open' : ''}`}>
          <div className="modal-box">
            <h3 className="font-bold text-lg text-brand-heading mb-4">
              {t('cancellation_information') || 'Información de Cancelación'}
            </h3>
            
            <div className="space-y-4">
              <div>
                <span className="font-semibold text-brand-body">
                  {t('name') || 'Nombre del usuario'}: 
                </span>
                <span className="ml-2 text-brand-caption">
                  {selectedCancelledAppointment.name}
                </span>
              </div>
              
              <div>
                <span className="font-semibold text-brand-body">
                  {t('phone_number') || 'Teléfono'}: 
                </span>
                <span className="ml-2 text-brand-caption">
                  {selectedCancelledAppointment.phone}
                </span>
              </div>

              <div>
                <span className="font-semibold text-brand-body">
                  {t('schedule_name') || 'Agenda'}: 
                </span>
                <span className="ml-2 text-brand-caption">
                  {selectedCancelledAppointment.scheduleName || 'General'}
                </span>
              </div>
              
              {selectedCancelledAppointment.cancel_at && (
                <div>
                  <span className="font-semibold text-brand-body">
                    {t('cancelled_at') || 'Cancelada el'}: 
                  </span>
                  <span className="ml-2 text-brand-caption">
                    {formatDate(selectedCancelledAppointment.cancel_at)}
                  </span>
                </div>
              )}
              
              {selectedCancelledAppointment.cancel_by && (
                <div>
                  <span className="font-semibold text-brand-body">
                    {t('cancelled_by') || 'Cancelada por'}: 
                  </span>
                  <span className="ml-2 text-brand-caption">
                    {selectedCancelledAppointment.cancel_by === 'HUMAN_AGENT' 
                      ? (t('human_agent') || 'Agente Humano')
                      : selectedCancelledAppointment.cancel_by}
                  </span>
                </div>
              )}
              
              <div className="divider"></div>
              
              <div>
                <span className="font-semibold text-brand-body">
                  {t('cancellation_reason') || 'Razón de cancelación'}: 
                </span>
                <div className="mt-2 p-3 bg-base-200 rounded-lg">
                  <p className="text-brand-body">
                    {selectedCancelledAppointment.cancel_reason}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="modal-action">
              <button 
                className="btn btn-primary" 
                onClick={handleCloseInfoDialog}
              >
                {t('close') || 'Cerrar'}
              </button>
            </div>
          </div>
          <div className="modal-backdrop" onClick={handleCloseInfoDialog}></div>
        </div>
      )}
    </div>
  );
};

export default ManageSchedules;
